import React, { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { whySection } from "../mock";

const WhySection = () => {
  const stageRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const el = stageRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = vh * 0.95;
      const end = vh * 0.3;
      const raw = (start - rect.top) / (start - end);
      setProgress(Math.min(1, Math.max(0, raw)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const rotateX = (1 - progress) * 42;
  const scale = 0.9 + progress * 0.1;
  const translateY = (1 - progress) * 40;

  const handlePlay = () => {
    setPlaying(true);
  };

  return (
    <section id="about" className="relative bg-[#EDF1F8] pt-10 pb-40 overflow-hidden">
      {/* strong blue glow framing the video */}
      <div
        className="pointer-events-none absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 h-[640px] w-[1120px] rounded-[40px] blur-[150px] opacity-90"
        style={{ background: "radial-gradient(closest-side, rgba(91,164,251,0.7), rgba(91,164,251,0.25) 60%, transparent)" }}
      />

      <div className="relative z-10 mx-auto max-w-[1100px] px-6 text-center">
        <h2 className="font-serif text-[#16263F] text-[10vw] md:text-[4rem] leading-[1] tracking-tight">
          {whySection.title}
        </h2>
        <p className="mx-auto mt-7 max-w-2xl text-[17px] leading-7 text-slate-400">
          {whySection.subtitle[0]}
          <br />
          {whySection.subtitle[1]}
        </p>
      </div>

      {/* 3D scroll-interactive video */}
      <div
        ref={stageRef}
        className="relative z-10 mx-auto mt-16 max-w-[1100px] px-6"
        style={{ perspective: "1600px" }}
      >
        <div
          className="relative rounded-[28px] overflow-hidden border-2 border-[#5BA4FB]/40 bg-slate-900 shadow-[0_0_70px_6px_rgba(91,164,251,0.55),0_40px_90px_rgba(20,40,70,0.25)] will-change-transform aspect-video"
          style={{
            transform: `rotateX(${rotateX}deg) scale(${scale}) translateY(${translateY}px)`,
            transformOrigin: "center top",
            transition: "transform 0.08s linear",
          }}
        >
          {playing ? (
            <iframe
              src={whySection.video}
              title="OpenPenguin Plattform Video"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              className="h-full w-full"
            />
          ) : (
            <button
              onClick={handlePlay}
              className="group absolute inset-0 h-full w-full"
              aria-label="Play video"
            >
              <img
                src={whySection.poster}
                alt="Why OpenPenguin"
                className="h-full w-full object-cover"
              />
              <span className="absolute inset-0 bg-black/15 transition-colors duration-300 group-hover:bg-black/25" />
              <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#5BA4FB] text-white shadow-[0_12px_40px_rgba(91,164,251,0.55)] transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-1 h-7 w-7 fill-current" />
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default WhySection;
